(ns ui.view.entity-creator.editor
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.field :as field]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.util :as uu]
   [ui.view.entity-creator.components :as c]
   [ui.view.entity-creator.model :as m]
   [ui.view.entity-creator.utils :as u]))

(defn editor-component
  [{:keys [ident attribute] :as args}
   {:keys [read-only? field-schema field-config
           entity pipeline value secondary-value]}]
  (let [label (loc/field-name attribute field-schema)
        placeholder (field/placeholder field-schema)
        entity-type (:entity/type entity)
        mandatory? (= (:setting field-config)
                      :mandatory)
        label (if mandatory?
                (str label " *")
                label)
        ;;TODO: test field-errors for editor
        errors @(rf/subscribe [m/field-errors
                               (assoc args :entity entity)])
        pipeline-id (:db/id pipeline)
        {:field/keys [data-type secondary-attribute secondary-field-schema
                      entity-types not-clearable? os-filter-terms-fn]} field-schema
        select-entity-types (field/select-entity-types field-schema)
        create-entity-types (field/create-entity-types field-schema)

        ;;TODO pipeline/target-asset-types when applicable:
        entity-types (if (= entity-types [:entity.type/asset])
                       @session/available-asset-types
                       entity-types)
        select-entity-types (if (= select-entity-types [:entity.type/asset])
                              @session/available-asset-types
                              select-entity-types)
        create-entity-types (if (= create-entity-types [:entity.type/asset])
                              @session/available-asset-types
                              create-entity-types)
        named-value-attribute (:named-value-attribute field-schema)
        currency? (= data-type :data.type/currency-value)
        secondary-value (if currency?
                          (or secondary-value
                              @session/default-currency
                              :eur)
                          secondary-value)
        ref-multi? (= data-type :data.type/ref-multi)
        search-input? (contains? data-types/ref-types data-type)
        value (if search-input?
                (u/val->option-val named-value-attribute value)
                value)
        edit! (fn [{:keys [conj? attribute]} val option-val]
                (let [value (if search-input?
                              option-val
                              val)]
                  (rf/dispatch-sync
                   [m/edit-value
                    (cond-> args
                      true (assoc :attribute attribute
                                  :entity entity
                                  :new-value value)
                      conj? (assoc :conj? true))])))
        primary-args (merge
                      ;; :editor.type/search data-input (i.e., open-search) requires
                      ;; some field-schema-specific args: making these explicit
                      ;; for now:
                      (when search-input?
                        (u/search-editor-args
                         {:entity-types entity-types
                          :select-entity-types select-entity-types
                          :not-clearable? not-clearable?
                          :os-filter-terms-fn os-filter-terms-fn
                          :pipeline-id pipeline-id}))
                      (when named-value-attribute
                        {:named-value-attribute named-value-attribute})
                      {:attribute attribute
                       :disabled? read-only?
                       :read-only? read-only?
                       :label label
                       :placeholder placeholder
                       :on-change (r/partial edit! {:attribute attribute})
                       :value value
                       :error (seq errors)
                       ;;:portal-target (some-> args :portal-target-atom deref)
                       :create! (when (u/allow-create?
                                       {:field-schema field-schema
                                        :entity-type entity-type
                                        :attribute attribute
                                        :pipeline-id pipeline-id})
                                  (fn [query]
                                    (f-entity/open-creator!
                                     create-entity-types
                                     {:named-value-attribute attribute
                                      :for-attribute attribute
                                      :for-entity-type entity-type
                                      :ident (into ident [:nested-editor attribute])
                                      :skip-success-msg? true
                                      :start-value query
                                      :on-ok!
                                      (fn [{:keys [new-entity-id
                                                   new-entity
                                                   close-cb!] :as _ok-args}]
                                        (edit! {:attribute attribute
                                                :conj? ref-multi?}
                                               new-entity-id
                                               {:value new-entity-id
                                                :label (entity/display-string
                                                        new-entity)})
                                        (close-cb!))})))
                       :options (u/resolve-options
                                 {:field-schema field-schema
                                  :pipeline-id pipeline-id})
                       :editor-type (when search-input?
                                      :editor.type/search)
                       :data-type data-type})
        secondary-args (when secondary-attribute
                         (assoc secondary-field-schema
                                :attribute secondary-attribute
                                :disabled? read-only?
                                :value secondary-value
                                :editor-type :editor.type/select
                                :on-change (r/partial edit!
                                                      {:attribute
                                                       secondary-attribute})))]
      ;;TODO remove after testing:
    #_(when (clojure.string/includes? label "huh")
        (prn ">custom-attr" attribute))
    [c/field-editor
     (cond-> primary-args
       secondary-args (assoc :secondary-args secondary-args))]))

(defn view
  ;; If you pass an explicit `:fields-config` override here, you MUST pass the
  ;; same `:fields-config` in the args used to subscribe to
  ;; `m/entity-validations` (or any sub depending on `m/config`) — otherwise
  ;; the rendered field set will diverge from the validated field set and the
  ;; confirm-button gating will be inconsistent with what the user sees.
  ;; `:force-read-only?` makes every field read-only, for a caller that blocks
  ;; editing for a while, for example during a save. It defaults to nil.
  [{:keys [entity-type config-path entity-edits-path] :as args}
   {:keys [raw-config field-schemas entity field-schema-overrides
           fields-config force-read-only?]}]
  (let [stage @(rf/subscribe [m/stage args])
        pipeline @(rf/subscribe [m/pipeline args])
        stages (->> (session/pipeline-stages pipeline)
                    (sort-by :common/ord))
        system-mandatory @(rf/subscribe [m/system-mandatory
                                         {:entity-type entity-type
                                          :pipeline pipeline}])
        fields-config (or fields-config
                          (u/resolve-entity-config
                           {:entity-type entity-type
                            :config-path config-path
                            :raw-config raw-config
                            :stages stages
                            :system-mandatory system-mandatory}))
        field-schemas (if (seq field-schema-overrides)
                        (reduce-kv (fn [acc attribute overrides]
                                     (update acc attribute
                                             (fn [fs]
                                               (potpuri/deep-merge fs overrides))))
                                   field-schemas
                                   field-schema-overrides)
                        field-schemas)
        stage-id (:db/id stage)
        fields (->> fields-config
                    :fields
                    (remove (fn [{:keys [attribute stages create]}]
                              (let [field-config (if stage-id
                                                   (get stages stage-id)
                                                   create)
                                    fs (get field-schemas attribute)]
                                (u/hidden-field? {:field-config field-config
                                                  :field-schema fs
                                                  :attribute attribute
                                                  :entity-type entity-type
                                                  :pipeline pipeline})))))]
    [:div
     {:class (uu/cm "flex flex-col gap-2")}
     (if (seq fields)
       (doall
        (for [{:keys [attribute stages create] :as _field} fields
              :let [field-config (if stage-id
                                   (get stages stage-id)
                                   create)
                    fs (get field-schemas attribute)
                    read-only? (or force-read-only?
                                   (:read-only? fs)
                                   (u/read-only-field? {:attribute attribute
                                                        :entity-type entity-type
                                                        :pipeline pipeline}))

                    value @(rf/subscribe [m/edited-value
                                          (assoc args
                                                 :attribute attribute
                                                 :entity-edits-path entity-edits-path
                                                 :entity entity)])
                    secondary-fs (some->> fs
                                          :field/secondary-attribute
                                          (get field-schemas))
                    fs (cond-> fs
                         secondary-fs (assoc :secondary-field-schema secondary-fs))]]
          ^{:key (str "field-input-" attribute)}
          [editor-component
           (assoc args
                  :attribute attribute)
           {:field-config field-config
            :field-schema fs
            :entity entity
            :value value
            :read-only? read-only?}]))
       [:div
        {:class (uu/cm "text-sm m-0 text-base-light-text-paler")}
        (loc/tr :creator/no-fields-configured)])]))
